<template>
  <section class="page" data-testid="page-project-internal">
    <header class="page-header">
      <div>
        <h1>project-internal</h1>
        <p>owner-only 的只读项目助手通道。</p>
      </div>
      <button class="button primary" :disabled="loading" @click="load">{{ loading ? "刷新中" : "刷新" }}</button>
    </header>

    <div class="metrics-grid">
      <MetricCard label="启用" :value="status?.enabled ? '是' : '否'" />
      <MetricCard label="模型" :value="status?.model || '-'" />
      <MetricCard label="最近耗时" :value="`${status?.last_latency_ms || 0}ms`" />
      <MetricCard label="执行能力" :value="status?.actions_enabled ? '开启' : '关闭'" />
    </div>

    <div class="split">
      <div class="panel">
        <h2>连接状态</h2>
        <p>Base URL：{{ status?.base_url || "-" }}</p>
        <p>Last Error：{{ status?.last_error || "-" }}</p>
        <p v-if="error" class="error-text">{{ error }}</p>
        <footer class="actions">
          <button class="button" :disabled="loading" @click="check">连接检查</button>
          <button class="button" :disabled="loading" @click="reset">重置会话</button>
        </footer>
        <pre v-if="checkResult">{{ JSON.stringify(checkResult, null, 2) }}</pre>
      </div>

      <div class="panel">
        <h2>测试提问</h2>
        <FormField label="问题"><textarea v-model="question" /></FormField>
        <button class="button primary" :disabled="testing || !question.trim()" @click="testAsk">{{ testing ? "请求中" : "发送" }}</button>
        <pre v-if="answer">{{ answer }}</pre>
      </div>
    </div>

    <div class="panel">
      <h2>上下文预览</h2>
      <p>{{ preview?.chars || 0 }} / {{ preview?.max_chars || 0 }} chars，truncated={{ preview?.truncated }}</p>
      <div v-if="preview?.risk_summary" class="risk-strip">
        <span>包含 {{ preview.risk_summary.included_sections || 0 }} 段</span>
        <span>裁剪 {{ preview.risk_summary.truncated_sections || 0 }} 段</span>
        <span>高敏 {{ (preview.risk_summary.sensitive_sections || []).length }} 段</span>
        <span>{{ preview.risk_summary.requires_owner ? "owner-only" : "非 owner-only" }}</span>
      </div>
      <div v-if="preview?.risk_summary?.sensitive_sections?.length" class="sensitive-list">
        <span v-for="section in preview.risk_summary.sensitive_sections" :key="section.name">
          {{ section.title }} · {{ section.chars }} chars
        </span>
      </div>
      <DataTable :columns="sectionColumns" :rows="preview?.sections || []">
        <template #included="{ row }">{{ row.included ? "是" : "否" }}</template>
        <template #truncated="{ row }">{{ row.truncated ? "是" : "否" }}</template>
      </DataTable>
    </div>

    <div class="panel">
      <h2>最近请求</h2>
      <DataTable :columns="requestColumns" :rows="status?.recent_requests || []" row-key="id">
        <template #ts="{ row }">{{ formatTime(row.ts) }}</template>
        <template #question="{ row }"><pre>{{ row.question }}</pre></template>
        <template #status="{ row }"><span class="status-badge" :class="row.status === 'ok' ? 'ok' : 'down'">{{ row.status }}</span></template>
      </DataTable>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { api, postJson } from "../api/client";
import type { OpenClawStatus } from "../api/types";
import { useToastStore } from "../stores/toast";
import DataTable from "../ui/DataTable.vue";
import FormField from "../ui/FormField.vue";
import MetricCard from "../ui/MetricCard.vue";

const toast = useToastStore();
const loading = ref(false);
const testing = ref(false);
const error = ref("");
const status = ref<OpenClawStatus | null>(null);
const preview = ref<any>(null);
const checkResult = ref<any>(null);
const question = ref("总结一下当前项目有哪些核心模块");
const answer = ref("");

const sectionColumns = [
  { key: "title", label: "来源" },
  { key: "chars", label: "字符" },
  { key: "included", label: "已包含" },
  { key: "truncated", label: "已裁剪" }
];
const requestColumns = [
  { key: "id", label: "ID" },
  { key: "ts", label: "时间" },
  { key: "status", label: "状态" },
  { key: "model", label: "模型" },
  { key: "context_chars", label: "上下文" },
  { key: "latency_ms", label: "耗时ms" },
  { key: "question", label: "问题" },
  { key: "error", label: "错误" }
];

function formatTime(ts: number) { return ts ? new Date(ts * 1000).toLocaleString() : "-"; }
async function load() {
  loading.value = true;
  error.value = "";
  try {
    status.value = await api<OpenClawStatus>("/api/project-internal/status");
    preview.value = await api("/api/project-internal/context-preview");
  } catch (err) {
    error.value = err instanceof Error ? err.message : "加载 project-internal 状态失败";
  } finally {
    loading.value = false;
  }
}
async function check() {
  loading.value = true;
  error.value = "";
  try {
    checkResult.value = await postJson("/api/project-internal/check", {});
  } catch (err) {
    error.value = err instanceof Error ? err.message : "连接检查失败";
  } finally {
    loading.value = false;
  }
}
async function reset() {
  loading.value = true;
  error.value = "";
  try {
    await postJson("/api/project-internal/reset", {});
    toast.push("project-internal 会话已重置");
    await load();
  } catch (err) {
    error.value = err instanceof Error ? err.message : "重置会话失败";
  } finally {
    loading.value = false;
  }
}
async function testAsk() {
  testing.value = true;
  error.value = "";
  try {
    const payload = await postJson<{ answer: string }>("/api/project-internal/test", { question: question.value });
    answer.value = payload.answer || "";
    await load();
  } catch (err) {
    error.value = err instanceof Error ? err.message : "project-internal 请求失败";
  } finally {
    testing.value = false;
  }
}
onMounted(load);
</script>

<style scoped>
.risk-strip,
.sensitive-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 10px 0 14px;
}
.risk-strip span,
.sensitive-list span {
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--panel-2);
  padding: 5px 9px;
}
.sensitive-list span {
  border-color: #78612d;
}
</style>
